import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { uploadApi } from '@/services/api';

export const ImageUpload = ({ 
  onUpload, 
  folder = "uploads",
  currentImage = null,
  className = "",
  aspectRatio = "aspect-video",
  uploadLabel = "Ladda upp bild",
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setPreview(currentImage || null);
  }, [currentImage]);

  const uploadToCloudinary = async (file) => {
    setUploading(true);
    
    try {
      // Get signature from backend
      const token = localStorage.getItem('byggfynd_token');
      if (!token) {
        throw new Error('Du måste vara inloggad för att ladda upp bilder');
      }
      const sigResponse = await uploadApi.getSignature(folder, 'image');
      const sig = sigResponse.data;
      
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      
      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      if (!uploadRes.ok) {
        throw new Error('Uppladdningen misslyckades');
      }
      
      const data = await uploadRes.json();
      const imageUrl = data.secure_url;
      
      setPreview(imageUrl);
      onUpload(imageUrl);
      toast.success('Bilden har laddats upp');
      
      return imageUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Kunde inte ladda upp bilden');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Endast bildfiler är tillåtna');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Bilden får max vara 10MB');
      return;
    }
    
    // Show preview immediately
    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
    
    // Upload to Cloudinary
    await uploadToCloudinary(file);
    
    // Clean up local preview
    URL.revokeObjectURL(localPreview);
  }, [folder, onUpload]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUpload(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleChange}
        className="hidden"
        id="image-upload"
      />
      
      {preview ? (
        <div className={`relative ${aspectRatio} bg-gray-100 rounded-md overflow-hidden group`}>
          <img 
            src={preview} 
            alt="Förhandsgranskning" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              Byt bild
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={uploading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
          )}
        </div>
      ) : (
        <div
          className={`${aspectRatio} border-2 border-dashed rounded-md transition-colors ${
            dragActive 
              ? 'border-[#FF8A00] bg-orange-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <div className="h-full flex flex-col items-center justify-center cursor-pointer p-4">
            {uploading ? (
              <>
                <Loader2 className="h-10 w-10 text-[#FF8A00] animate-spin mb-2" />
                <p className="text-sm text-gray-500">Laddar upp...</p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Upload className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-700 mb-1">
                  {uploadLabel}
                </p>
                <p className="text-xs text-gray-500 mb-3">
                  Dra och släpp eller klicka för att välja
                </p>
                <p className="text-xs text-gray-400">
                  PNG, JPG, WebP (max 10MB)
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Multi-image upload component
export const MultiImageUpload = ({ 
  images = [], 
  onImagesChange, 
  folder = "uploads",
  maxImages = 5 
}) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (file) => {
    if (images.length >= maxImages) {
      toast.error(`Max ${maxImages} bilder tillåtna`);
      return;
    }
    
    setUploading(true);
    
    try {
      const token = localStorage.getItem('byggfynd_token');
      if (!token) {
        throw new Error('Du måste vara inloggad för att ladda upp bilder');
      }
      
      const sigResponse = await uploadApi.getSignature(folder, 'image');
      const sig = sigResponse.data;
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      
      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      if (!uploadRes.ok) {
        const uploadError = await uploadRes.json().catch(() => ({}));
        throw new Error(uploadError.error?.message || 'Uppladdningen till Cloudinary misslyckades');
      }
      
      const data = await uploadRes.json();
      onImagesChange([...images, data.secure_url]);
      toast.success('Bilden har laddats upp');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Kunde inte ladda upp bilden');
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    if (e.target.files) {
      Array.from(e.target.files).forEach(file => handleUpload(file));
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {images.map((url, index) => (
          <div key={index} className="relative aspect-square bg-gray-100 rounded-md overflow-hidden group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeImage(index)}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        
        {images.length < maxImages && (
          <div
            className="aspect-square border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors"
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
            ) : (
              <Upload className="h-6 w-6 text-gray-400" />
            )}
          </div>
        )}
      </div>
      
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleChange}
        className="hidden"
      />
      
      <p className="text-xs text-gray-500">
        {images.length}/{maxImages} bilder
      </p>
    </div>
  );
};
